import React, { useRef, useCallback, useMemo, useEffect } from 'react';
import { Formik, Form as FormikForm } from 'formik';
import clsx from 'clsx';
import { cloneDeep, get, isEmpty, isEqual, set } from 'lodash';

import { transformApiErrorToFormik } from '@dashboard/services/form';
import { useUnsavedChanges } from '@dashboard/components/UnsavedChanges';

import Label from '@dashboard/components/Form/components/Label';
import Divider from '@dashboard/components/Divider';

import { FormErrorBase, FormErrorFromProps } from './components/FormError';
import FormSubmit from './components/FormSubmit';
import CanUseFeature from '@dashboard/components/CanUseFeature';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import Switch from './components/Switch/Switch';
import AutoSave from './components/AutoSave';
import { ColorSelectField } from './components/ColorSelect';
import LexicalRichTextEditor from './components/LexicalRichTextEditor';
import { EmailField } from './components/EmailField';
import ListboxField from './components/Listbox';
import FieldGroup from './components/FieldGroup';
import Columns from './components/Columns';
import OptionList from './components/OptionList';
import TextInput from './components/TextInput';
import Textarea from './components/Textarea';
import Select from './components/Select';
import Checkbox from './components/Checkbox';
import Radio from './components/Radio';
import { ColorPickerField } from './components/ColorPicker';
import CurrencySelector from './components/CurrencySelector';
import { ImageField } from './components/Image';
import PageSelect from './components/PageSelect';
import RadioCard from './components/RadioCard';
import CSSEditor from './components/CSSEditor';
import WebsiteInput from './components/WebsiteInput';
import AccordionGroupField from './components/AccordionGroupField';
import Disclosure from './components/Disclosure';
import ConditionalWrapper from './components/ConditionalWrapper';
import Heading from './components/Heading';
import {
  FormExtensionContext,
  getNestedValue,
  fieldValueEqual,
  getIsFieldHidden,
} from './formUtils';

export {
  FormExtensionContext,
  getNestedValue,
  fieldValueEqual,
  getIsFieldHidden,
} from './formUtils';
export { renderFields } from './formUtils';

export function getFieldComponentByType(fieldType) {
  switch (fieldType) {
    case 'divider':
      return Divider;
    case 'fieldgroup':
      return FieldGroup;
    case 'columns':
      return Columns;
    case 'email':
      return EmailField;
    case 'number':
    case 'url':
    case 'text':
      return TextInput;
    case 'website':
      return WebsiteInput;
    case 'textarea':
      return Textarea;
    case 'css-editor':
      return CSSEditor;
    case 'rich-text':
      return LexicalRichTextEditor;
    case 'select':
      return Select;
    case 'checkbox':
      return Checkbox;
    case 'radio':
      return Radio;
    case 'color':
      return ColorPickerField;
    case 'color-select':
      return ColorSelectField;
    case 'currency':
      return CurrencySelector;
    case 'image':
      return ImageField;
    case 'pageSelect':
      return PageSelect;
    case 'radioCard':
      return RadioCard;
    case 'optionList':
      return OptionList;
    case 'listbox':
      return ListboxField;
    default:
      return TextInput;
  }
}

function FormField({
  field,
  values,
  inputClassName,
  setFieldValue,
  touched,
  initialValues,
  errors,
  renderField,
}) {
  // Extension point: try custom renderer first
  if (renderField) {
    const result = renderField(field, { values, setFieldValue, inputClassName, errors, touched });
    if (result !== null) return result;
  }

  switch (field.type) {
    case 'label': {
      return (
        <Label
          htmlFor={field.id || field.name}
          optional={field.optional && !field.hideOptional}
          tooltip={field.tooltip}
          tags={field.tags}
          className={field.className}
        >
          {field.label}
        </Label>
      );
    }
    case 'custom': {
      return field.renderField(field, { setFieldValue, values });
    }
    case 'radioIconCard':
      return (
        <RadioCard.Group
          name={field.name}
          columns={field.columns}
          grid={field.grid}
          className={inputClassName}
        >
          {field.options.map((option, index) => (
            <div
              key={option.value}
              className={clsx({
                'text-center': option.center,
              })}
            >
              <RadioCard.Card
                key={index}
                value={option.value}
                checked={field.value === option.value}
                required={field.required}
                name={field.name}
                onChange={field.onChange}
                center={option.center}
              >
                {option.component && <option.component />}

                {option.label && (
                  <div className="mt-2 font-normal text-gray-900 dark:text-foreground text-sm">
                    {option.label}
                  </div>
                )}
                {option.labelDescription && (
                  <RadioCard.LabelDescription>{option.labelDescription}</RadioCard.LabelDescription>
                )}
              </RadioCard.Card>
            </div>
          ))}
        </RadioCard.Group>
      );
    case 'radioCard':
      return (
        <RadioCard.Group
          name={field.name}
          columns={field.columns}
          grid={field.grid}
          className={inputClassName}
        >
          {field.options.map((option, index) => (
            <RadioCard.Card
              key={index}
              value={option.value}
              checked={field.value === option.value}
              required={field.required}
              name={field.name}
              onChange={field.onChange}
              disabled={option.disabled}
            >
              {option.component && <option.component />}

              {option.label && <RadioCard.Label>{option.label}</RadioCard.Label>}

              {option.labelDescription && (
                <RadioCard.LabelDescription>{option.labelDescription}</RadioCard.LabelDescription>
              )}
            </RadioCard.Card>
          ))}
        </RadioCard.Group>
      );
    case 'radio':
      return (
        <Radio.Group name={field.name} className={inputClassName}>
          {field.options.map((option, index) => (
            <Radio.Wrapper key={index} hasHelpText={!!option.helpText}>
              <Radio.Input
                value={option.value}
                required={field.required}
                name={field.name}
                id={`${field.name}-${option.value}`}
                hasHelpText={!!option.helpText}
              />

              <Radio.Label htmlFor={`${field.name}-${option.value}`} helpText={option.helpText}>
                {option.label}
              </Radio.Label>
            </Radio.Wrapper>
          ))}
        </Radio.Group>
      );
    case 'checkbox': {
      const { showRequired, ...checkboxProps } = field;

      return (
        <Checkbox.Wrapper>
          <Checkbox
            {...checkboxProps}
            name={field.name}
            id={field.name}
            className={clsx(field.className, inputClassName)}
          />
          <Checkbox.Label
            htmlFor={field.name}
            tooltip={field.tooltip}
            required={showRequired && field.required}
          >
            {field.label}
          </Checkbox.Label>
        </Checkbox.Wrapper>
      );
    }
    case 'select':
      return (
        <Select
          name={field.name}
          id={field.name}
          // An unset value renders as what the page renders for it, the
          // registry default, not as whichever option happens to sit first.
          value={getNestedValue(values, field.name) ?? field.defaultValue}
          disabled={field.disabled}
          required={field.required}
          placeholder={field.placeholder}
          options={field.options}
          className={clsx(field.className, inputClassName)}
          onChange={async e => {
            const newValue = e.target.value;
            if (field.onChange) {
              const shouldProceed = (await field.onChange(e)) ?? true;
              if (!shouldProceed) return;
            }
            const value = field.transformValue ? field.transformValue(newValue) : newValue;
            setFieldValue(field.name, value, true);
            // A value that makes another field's value impossible writes that
            // field too, so the form never keeps a setting the chosen value
            // has already overruled — see `setAlso` in themePropertyRegistry.
            Object.entries(field.setAlso?.[String(value)] || {}).forEach(([path, sideValue]) =>
              setFieldValue(path, sideValue),
            );
          }}
        />
      );
    case 'color-select':
      return (
        <div className="flex items-center gap-x-2 relative">
          <Label isInline className="truncate">
            {field.label}
          </Label>
          <ColorSelectField {...field} />
        </div>
      );
    case 'toggle': {
      // Locked: show a lock where the switch would be (the row is the upgrade-popover
      // trigger via CanUseFeature) instead of stacking a lock on top of the switch.
      const toggleControl = field.locked ? (
        <div className="flex items-center justify-between gap-x-4 flex-1">
          <span className="flex-grow flex flex-col">
            <Label className="!mb-0 text-sm font-medium text-gray-500 dark:text-muted-foreground">
              {field.label}
            </Label>
          </span>
          <LockClosedIcon className="h-4 w-4 text-gray-400 dark:text-muted-foreground shrink-0" />
        </div>
      ) : (
        <Switch
          name={field.name}
          id={field.id || field.name}
          label={field.label}
          description={field.description}
          value={getNestedValue(values, field.name)}
          required={field.required}
          onChange={(name, value) => {
            setFieldValue(field.name, value);
            if (field.onChange) field.onChange(value);
          }}
          size={field.size}
        />
      );

      // `dividers`: self-contained top + bottom rules with symmetric padding. Padding
      // doesn't margin-collapse, so the row is guaranteed equidistant from both lines —
      // unlike relying on separate divider fields whose mb-4 always points one way.
      if (field.dividers) {
        return (
          <div className="border-y border-gray-100 dark:border-border py-4">{toggleControl}</div>
        );
      }
      return toggleControl;
    }
    case 'columns': {
      return (
        <Columns
          {...field}
          values={values}
          setFieldValue={setFieldValue}
          inputClassName={inputClassName}
          errors={errors}
          touched={touched}
        />
      );
    }
    case 'fieldgroup': {
      return (
        <FieldGroup
          {...field}
          values={values}
          setFieldValue={setFieldValue}
          initialValues={initialValues}
          inputClassName={inputClassName}
          errors={errors}
          touched={touched}
        />
      );
    }
    case 'disclosure': {
      return (
        <Disclosure
          {...field}
          values={values}
          setFieldValue={setFieldValue}
          inputClassName={inputClassName}
          errors={errors}
          touched={touched}
        />
      );
    }
    case 'accordionGroup': {
      return (
        <AccordionGroupField
          {...field}
          values={values}
          setFieldValue={setFieldValue}
          inputClassName={inputClassName}
          errors={errors}
          touched={touched}
        />
      );
    }
    case 'conditional-wrapper': {
      return (
        <ConditionalWrapper
          enabled={field.enabled}
          className={field.className}
          values={values}
          setFieldValue={setFieldValue}
          touched={touched}
          errors={errors}
        >
          {field.children}
        </ConditionalWrapper>
      );
    }
    case 'heading': {
      return <Heading {...field} />;
    }
    case 'currency': {
      return (
        <CurrencySelector
          name={field.name}
          id={field.id || field.name}
          value={getNestedValue(values, field.name)}
          disabled={field.disabled}
          className={inputClassName}
          onChange={e => {
            setFieldValue(field.name, e.target.value);
            if (field.onChange) field.onChange(e);
          }}
        />
      );
    }
    default: {
      const FieldComponent = getFieldComponentByType(field.type);

      const { hidePlaceHolder, showRequired: _showRequired, ...rest } = field;

      return React.createElement(FieldComponent, {
        ...rest,
        name: rest.name,
        id: rest.id || rest.name,
        placeholder: hidePlaceHolder ? '' : rest.placeholder || rest.label,
        defaultValue: rest.defaultValue,
        className: inputClassName,
      });
    }
  }
}

function forEachRequiredSelect(fieldsArray, values, visit, skipHidden) {
  fieldsArray?.forEach(field => {
    if (!field) return;
    if (skipHidden && getIsFieldHidden(field, values)) return;

    if (field.type === 'select' && field.required && field.name) visit(field);

    if (field.fields) {
      const nestedFields =
        typeof field.fields === 'function'
          ? field.fields({ values, setFieldValue: () => {}, touched: {} })
          : field.fields;
      forEachRequiredSelect(nestedFields, values, visit, skipHidden);
    }
  });
}

// Radix Select's hidden native <select> auto-selects its first option when the
// value is empty, so the browser's own `required` check never fails for it.
function collectRequiredSelectErrors(fieldsArray, values) {
  const errors = {};

  forEachRequiredSelect(
    fieldsArray,
    values,
    field => {
      const value = getNestedValue(values, field.name);
      if (value !== '' && value !== undefined && value !== null) return;

      // set(), not errors[name]: a dotted name has to nest, because the field
      // reads its error back with getNestedValue.
      set(
        errors,
        field.name,
        field.placeholder
          ? `Please ${field.placeholder.charAt(0).toLowerCase()}${field.placeholder.slice(1)}`
          : 'Please select an option',
      );
    },
    true,
  );

  return errors;
}

// Formik marks fields touched from the keys it finds in values, and an error
// only renders once its field is touched. A required select missing from
// initialValues would therefore block submit with nothing shown, so give it a
// key. Returns the original object when there is nothing to seed, because
// Formik reinitialises on an identity change.
// Validation only ever reports empty required selects, so a form without any can
// skip rebuilding its field list on every change. The design forms build hundreds
// of registry-driven fields per call, and never a required select.
function hasRequiredSelect(fieldsArray, values) {
  let found = false;
  forEachRequiredSelect(
    fieldsArray,
    values,
    () => {
      found = true;
    },
    false,
  );
  return found;
}

function withRequiredSelectDefaults(fieldsArray, values) {
  if (!values) return values;

  const missing = [];

  forEachRequiredSelect(
    fieldsArray,
    values,
    field => {
      if (getNestedValue(values, field.name) === undefined) missing.push(field.name);
    },
    false,
  );

  if (missing.length === 0) return values;

  const seeded = cloneDeep(values);
  missing.forEach(name => set(seeded, name, ''));
  return seeded;
}

const Form = ({
  initialValues,
  fields = [],
  formSubmitDirection = 'row',
  formSubmitAlign = 'start',
  submitButton = {
    label: 'Save',
    appearance: 'primary',
    size: 'base',
    disabled: false,
  },
  cancelButton = null,
  inputClassName = '',
  onSubmit,
  formSubmit = null,
  showState = true,
  children,
  onValuesChange = () => null,
  onDirty = () => null,
  validationSchema = null,
  autosave = false,
  autosaveDebounceMs = 0,
  renderField = null,
  customFieldPropsAreEqual = null,
  customLeafFieldNames = null,
  customContainerChildrenPropsEqual = null,
  customContainerValuesEqual = null,
  fieldsContainerClassName = null,
  ...props
}) => {
  const formIdRef = useRef(`form-${Math.random().toString(36).substr(2, 9)}`);

  // Stable callback refs — prevent Formik re-renders from prop identity changes
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;

  const onValuesChangeRef = useRef(onValuesChange);
  onValuesChangeRef.current = onValuesChange;

  const onDirtyRef = useRef(onDirty);
  onDirtyRef.current = onDirty;

  const unsavedChanges = useUnsavedChanges();
  // A form that does not reset after saving still reads dirty; it holds nothing
  // unsaved until its values move away from what was last saved.
  const savedValuesRef = useRef(undefined);

  useEffect(() => {
    const formId = formIdRef.current;
    return () => unsavedChanges?.report(formId, false);
  }, [unsavedChanges]);

  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;

  const stableOnSubmit = useCallback(async (values, actions) => {
    actions.setStatus(undefined);

    const { data, error, onSuccess, onError } = await onSubmitRef.current(values, actions);

    if (data) {
      actions.setStatus('');
      savedValuesRef.current = cloneDeep(values);

      if (onSuccess) {
        onSuccess(data);
      }

      return true;
    } else {
      const { status, errors } = transformApiErrorToFormik(error);

      if (!isEmpty(errors)) {
        actions.setErrors(errors);
      } else {
        actions.setStatus(status);
      }

      if (onError) {
        onError(error);
      }

      return false;
    }
  }, []);

  // Read off the last render's field list; true until then so nothing is skipped
  // before the form has drawn once.
  const hasRequiredSelectsRef = useRef(true);

  const stableValidate = useCallback(values => {
    onValuesChangeRef.current(values);

    if (!hasRequiredSelectsRef.current) return {};

    const fieldsArray =
      typeof fieldsRef.current === 'function'
        ? fieldsRef.current({ values, setFieldValue: () => {}, touched: {} })
        : fieldsRef.current;

    return collectRequiredSelectErrors(fieldsArray, values);
  }, []);

  const seededInitialValues = useMemo(() => {
    const fieldsArray =
      typeof fields === 'function'
        ? fields({ values: initialValues, setFieldValue: () => {}, touched: {} })
        : fields;

    return withRequiredSelectDefaults(fieldsArray, initialValues);
  }, [fields, initialValues]);

  const extensionValue = useMemo(
    () => ({
      renderField,
      customFieldPropsAreEqual,
      customLeafFieldNames,
      customContainerChildrenPropsEqual,
      customContainerValuesEqual,
    }),
    [
      renderField,
      customFieldPropsAreEqual,
      customLeafFieldNames,
      customContainerChildrenPropsEqual,
      customContainerValuesEqual,
    ],
  );

  return (
    <FormExtensionContext.Provider value={extensionValue}>
      <Formik
        {...props}
        initialValues={seededInitialValues}
        onSubmit={stableOnSubmit}
        validate={stableValidate}
        validationSchema={validationSchema}
        validateOnBlur={props.validateOnBlur === true ? true : !!autosave}
        validateOnChange={props.validateOnChange === true ? true : !!autosave}
      >
        {({
          dirty,
          status,
          values,
          setFieldValue,
          handleSubmit,
          errors,
          touched,
          initialValues,
        }) => {
          onDirtyRef.current(dirty);
          if (unsavedChanges && !autosave) {
            unsavedChanges.report(
              formIdRef.current,
              dirty && !isEqual(values, savedValuesRef.current),
            );
          }

          const fieldsArray =
            typeof fields === 'function' ? fields({ values, setFieldValue, touched }) : fields;

          hasRequiredSelectsRef.current = hasRequiredSelect(fieldsArray, values);

          const getAllFieldNames = fieldsArray => {
            const names = [];

            fieldsArray?.forEach(field => {
              if (!field) return;
              if (field.name) {
                names.push(field.name);
              }
              if (customLeafFieldNames) {
                names.push(...customLeafFieldNames(field));
              }
              if (field.fields) {
                const nestedFields =
                  typeof field.fields === 'function'
                    ? field.fields({ values, setFieldValue, touched })
                    : field.fields;

                names.push(...getAllFieldNames(nestedFields));
              }
            });
            return names;
          };
          const allFieldNames = getAllFieldNames(fieldsArray);

          const errorsWithoutField = Object.entries(errors)
            .filter(([key]) => !allFieldNames.includes(key))
            .map(([, value]) => value);

          const firstError = errorsWithoutField.length ? errorsWithoutField[0] : undefined;

          const visibleFields = fieldsArray.filter(Boolean);

          const renderedFields = visibleFields.map((field, index) => {
            const nextField = visibleFields
              .slice(index + 1)
              .find(f => !getIsFieldHidden(f, values));
            const fieldKey = field.name || field.id || `field-${index}`;
            const fieldNode = (
              <Field
                field={{
                  ...field,
                  visible: field.visible === undefined ? true : field.visible,
                }}
                nextFieldType={nextField?.type}
                nextFieldInset={nextField?.inset}
                values={values}
                setFieldValue={setFieldValue}
                formId={formIdRef.current}
                inputClassName={inputClassName}
                touched={touched}
                initialValues={initialValues}
                errors={errors}
                customFieldPropsAreEqual={customFieldPropsAreEqual}
                customLeafFieldNames={customLeafFieldNames}
                customContainerChildrenPropsEqual={customContainerChildrenPropsEqual}
                customContainerValuesEqual={customContainerValuesEqual}
              />
            );

            // A field can opt into the plan-gated upgrade overlay (lock icon +
            // popover) by setting `locked` + `feature` (+ optional `lockDescription`).
            if (field.locked) {
              return (
                <CanUseFeature
                  key={fieldKey}
                  feature={field.feature}
                  isDisabled
                  showIcon={false}
                  reduceOpacity={false}
                  description={field.lockDescription}
                >
                  {fieldNode}
                </CanUseFeature>
              );
            }

            return React.cloneElement(fieldNode, { key: fieldKey });
          });

          return (
            <FormikForm>
              {fieldsContainerClassName ? (
                <div className={fieldsContainerClassName}>{renderedFields}</div>
              ) : (
                renderedFields
              )}

              {children}

              {status && typeof status === 'string' && <FormErrorBase>{status}</FormErrorBase>}
              {firstError && typeof firstError === 'string' && (
                <FormErrorBase>{firstError}</FormErrorBase>
              )}

              {autosave && <AutoSave showState={showState} debounceMs={autosaveDebounceMs} />}

              {formSubmit
                ? typeof formSubmit === 'function'
                  ? formSubmit({
                      dirty,
                      handleSubmit,
                    })
                  : formSubmit
                : null}

              {!autosave && !formSubmit && (
                <FormSubmit
                  isInline={!showState}
                  align={formSubmitAlign}
                  direction={formSubmitDirection}
                  hasMargin={fields.length > 0 || status || firstError}
                >
                  {formSubmitAlign === 'end' && (
                    <>
                      {showState && <FormSubmit.State visible={!!dirty} />}

                      {cancelButton && (
                        <FormSubmit.Cancel
                          appearance={cancelButton.appearance}
                          size={cancelButton.size}
                          onClick={cancelButton.onClick}
                        >
                          {cancelButton.label}
                        </FormSubmit.Cancel>
                      )}

                      <FormSubmit.Button
                        appearance={submitButton.appearance}
                        size={submitButton.size}
                        disabled={submitButton.disabled}
                        button={submitButton.button}
                        className={submitButton.className}
                      >
                        {submitButton.label}
                      </FormSubmit.Button>
                    </>
                  )}

                  {formSubmitAlign === 'start' && (
                    <>
                      {cancelButton && (
                        <FormSubmit.Cancel
                          appearance={cancelButton.appearance}
                          size={cancelButton.size}
                          onClick={cancelButton.onClick}
                        >
                          {cancelButton.label}
                        </FormSubmit.Cancel>
                      )}

                      <FormSubmit.Button
                        appearance={submitButton.appearance}
                        size={submitButton.size}
                        disabled={submitButton.disabled}
                        button={submitButton.button}
                        className={submitButton.className}
                      >
                        {submitButton.label}
                      </FormSubmit.Button>

                      {showState && <FormSubmit.State visible={!!dirty} />}
                    </>
                  )}
                </FormSubmit>
              )}
            </FormikForm>
          );
        }}
      </Formik>
    </FormExtensionContext.Provider>
  );
};

const CONTAINER_FIELD_TYPES = new Set([
  'columns',
  'fieldgroup',
  'disclosure',
  'accordionGroup',
  'conditional-wrapper',
  'font-group-columns',
]);

function getLeafFieldNames(fields, customLeafFieldNames) {
  const names = [];
  if (!fields || typeof fields === 'function') return names;
  for (const field of fields) {
    if (!field) continue;
    if (field.name && !CONTAINER_FIELD_TYPES.has(field.type) && field.type !== 'custom') {
      names.push(field.name);
    }
    // Extension point: extra leaf names from custom field structures
    if (customLeafFieldNames) {
      names.push(...customLeafFieldNames(field));
    }
    if (field.fields && typeof field.fields !== 'function') {
      names.push(...getLeafFieldNames(field.fields, customLeafFieldNames));
    }
    if (field.children && Array.isArray(field.children)) {
      names.push(...getLeafFieldNames(field.children, customLeafFieldNames));
    }
    // Accordion groups store children in items[].fields
    if (field.items && Array.isArray(field.items)) {
      for (const item of field.items) {
        if (item.fields) {
          names.push(...getLeafFieldNames(item.fields, customLeafFieldNames));
        }
      }
    }
  }
  return names;
}

function containerChildrenStructureEqual(
  prevFields,
  nextFields,
  customContainerChildrenPropsEqual,
) {
  if (prevFields === nextFields) return true;
  if (!Array.isArray(prevFields) || !Array.isArray(nextFields)) return false;
  if (prevFields.length !== nextFields.length) return false;
  for (let i = 0; i < prevFields.length; i++) {
    const pf = prevFields[i];
    const nf = nextFields[i];
    if (!pf || !nf) {
      if (pf !== nf) return false;
      continue;
    }
    if (
      pf.name !== nf.name ||
      pf.type !== nf.type ||
      pf.label !== nf.label ||
      pf.disabled !== nf.disabled
    )
      return false;
    // Extension point: extra child structure checks
    if (customContainerChildrenPropsEqual) {
      if (!customContainerChildrenPropsEqual(pf, nf)) return false;
    }
  }
  return true;
}

function containerPropsEqual(pf, nf) {
  return (
    pf.enabled === nf.enabled &&
    pf.columns === nf.columns &&
    pf.gap === nf.gap &&
    pf.className === nf.className &&
    pf.divider === nf.divider &&
    pf.defaultOpen === nf.defaultOpen &&
    pf.isRelevant === nf.isRelevant &&
    pf.autoClose === nf.autoClose &&
    pf.fontGroupHasChanges === nf.fontGroupHasChanges
  );
}

function fieldPropsAreEqual(prevProps, nextProps) {
  const pf = prevProps.field;
  const nf = nextProps.field;

  if (prevProps.nextFieldType !== nextProps.nextFieldType) return false;
  if (prevProps.nextFieldInset !== nextProps.nextFieldInset) return false;

  // Container types — compare own props, children structure, and descendant values
  // This check comes BEFORE the nameless check because some containers (columns,
  // fieldgroup, conditional-wrapper) don't have names but still contain valued children.
  if (CONTAINER_FIELD_TYPES.has(nf.type)) {
    if (pf.type !== nf.type) return false;
    if (!containerPropsEqual(pf, nf)) return false;

    // Check visibility change for container types
    const prevContainerHidden = getIsFieldHidden(pf, prevProps.values);
    const nextContainerHidden = getIsFieldHidden(nf, nextProps.values);
    if (prevContainerHidden !== nextContainerHidden) return false;

    let prevChildren, nextChildren;
    if (nf.type === 'conditional-wrapper') {
      prevChildren = pf.children;
      nextChildren = nf.children;
    } else if (nf.type === 'accordionGroup') {
      // Accordion groups store children in items[].fields, not in fields directly
      prevChildren = pf.items?.flatMap(item => item.fields || []);
      nextChildren = nf.items?.flatMap(item => item.fields || []);
    } else {
      prevChildren = pf.fields;
      nextChildren = nf.fields;
    }

    // If fields is a function, always re-render (dynamic children)
    if (typeof prevChildren === 'function' || typeof nextChildren === 'function') return false;

    if (
      !containerChildrenStructureEqual(
        prevChildren,
        nextChildren,
        nextProps.customContainerChildrenPropsEqual,
      )
    )
      return false;

    // Compare descendant leaf field values, errors, and touched state
    const leafNames = getLeafFieldNames(nextChildren, nextProps.customLeafFieldNames);
    for (const leafName of leafNames) {
      const prevValue = getNestedValue(prevProps.values, leafName);
      const nextValue = getNestedValue(nextProps.values, leafName);
      if (!fieldValueEqual(prevValue, nextValue)) return false;
      if (getNestedValue(prevProps.errors, leafName) !== getNestedValue(nextProps.errors, leafName))
        return false;
      if (
        getNestedValue(prevProps.touched, leafName) !== getNestedValue(nextProps.touched, leafName)
      )
        return false;
    }

    // Extension point: custom container value checks (e.g., color token sources)
    if (nextProps.customContainerValuesEqual) {
      const result = nextProps.customContainerValuesEqual(prevProps.values, nextProps.values);
      if (result === false) return false;
    }

    // Check if any child field's visibility changed
    if (Array.isArray(nextChildren)) {
      for (let i = 0; i < nextChildren.length; i++) {
        if (!prevChildren[i] || !nextChildren[i]) continue;
        const prevChildHidden = getIsFieldHidden(prevChildren[i], prevProps.values);
        const nextChildHidden = getIsFieldHidden(nextChildren[i], nextProps.values);
        if (prevChildHidden !== nextChildHidden) return false;
      }
    }

    if (prevProps.inputClassName !== nextProps.inputClassName) return false;
    return true;
  }

  // Nameless non-container fields (headings, dividers) — compare by content
  if (!pf.name || !nf.name) {
    // Custom fields render arbitrary closures whose captured values (e.g. the
    // price block's interval/repeats state) aren't visible to this memo — always
    // re-render, mirroring the named-custom rule below. Without this a nameless
    // custom is memoized by `id` alone and shows a stale closure (the "Repeats"
    // select appeared stuck). Re-render (not remount: `id`/key is unchanged) so
    // focus in any child input is preserved.
    if (nf.type === 'custom' || pf.type === 'custom') return false;

    const base =
      pf.type === nf.type && pf.label === nf.label && pf.enabled === nf.enabled && pf.id === nf.id;
    if (!base) return false;

    if (getIsFieldHidden(pf, prevProps.values) !== getIsFieldHidden(nf, nextProps.values)) {
      return false;
    }

    // Extension point: custom comparator for nameless fields
    if (nextProps.customFieldPropsAreEqual) {
      const result = nextProps.customFieldPropsAreEqual(prevProps, nextProps);
      if (result === false) return false;
    }

    return true;
  }

  if (pf.name !== nf.name || pf.type !== nf.type) return false;

  // Custom type uses arbitrary render functions — always re-render. An optionList's
  // options and per-option fields are built from sibling values, so it re-renders too.
  if (nf.type === 'custom' || nf.type === 'optionList') return false;

  // Check this field's specific value in the form state
  const prevValue = getNestedValue(prevProps.values, pf.name);
  const nextValue = getNestedValue(nextProps.values, nf.name);
  if (!fieldValueEqual(prevValue, nextValue)) return false;

  // Check error/touched state for this field
  if (getNestedValue(prevProps.errors, pf.name) !== getNestedValue(nextProps.errors, nf.name))
    return false;
  if (getNestedValue(prevProps.touched, pf.name) !== getNestedValue(nextProps.touched, nf.name))
    return false;

  // Check visibility change (handles boolean and function-based visibility)
  const prevHidden = getIsFieldHidden(pf, prevProps.values);
  const nextHidden = getIsFieldHidden(nf, nextProps.values);
  if (prevHidden !== nextHidden) return false;

  // For select/listbox fields, also check if options changed
  if (nf.type === 'select' || nf.type === 'listbox') {
    if (pf.options?.length !== nf.options?.length) return false;
    if (
      pf.options?.some(
        (opt, i) => opt.value !== nf.options[i]?.value || opt.label !== nf.options[i]?.label,
      )
    )
      return false;
  }

  // Check generic field config properties that affect rendering
  if (pf.label !== nf.label) return false;
  if (pf.enabled !== nf.enabled) return false;
  if (pf.disabled !== nf.disabled) return false;
  if (pf.required !== nf.required) return false;

  // Extension point: custom comparator for named fields
  if (nextProps.customFieldPropsAreEqual) {
    const result = nextProps.customFieldPropsAreEqual(prevProps, nextProps);
    if (result === false) return false;
  }

  if (prevProps.inputClassName !== nextProps.inputClassName) return false;

  return true;
}

function FieldInner({
  field,
  nextFieldType,
  nextFieldInset,
  values,
  inputClassName,
  setFieldValue,
  touched,
  initialValues,
  errors,
}) {
  const { renderField } = React.useContext(FormExtensionContext);

  const showLabel =
    !!field.label &&
    !field.hideLabel &&
    field.type !== 'checkbox' &&
    field.type !== 'toggle' &&
    field.type !== 'disclosure' &&
    field.type !== 'label' &&
    field.type !== 'columns' &&
    field.type !== 'color-select' &&
    field.type !== 'heading' &&
    field.type !== 'divider';

  const isFieldHidden = getIsFieldHidden(field, values);

  const isReduceMargin =
    typeof field.reduceMargin === 'function' ? field.reduceMargin(values) : field.reduceMargin;

  const isDesignFormField = showLabel && field.isDesignForm;
  const isNoMargin = field.noMargin || isDesignFormField;
  const isRegularMargin = !isReduceMargin && !field.noMargin;

  // Declarative-path equivalent of Checkbox.Group: 8px between consecutive checkbox
  // fields (mb-2! below). Keep this in sync with OPTION_GROUP_CLASSNAME (gap-2) used by
  // the manual Checkbox.Group / Radio.Group so both surfaces stay at the same row gap.
  // Applies under both regular and reduceMargin (e.g. OptionList) contexts; excluded
  // under noMargin/increaseMargin where the field opted out of the margin rhythm.
  const isCheckboxFollowedByCheckbox =
    field.type === 'checkbox' &&
    nextFieldType === 'checkbox' &&
    !isNoMargin &&
    !field.increaseMargin;

  const isCheckboxRevealingInset =
    field.type === 'checkbox' && nextFieldInset && !isNoMargin && !field.increaseMargin;

  const isTightCheckboxGap = isCheckboxFollowedByCheckbox || isCheckboxRevealingInset;

  const fieldLabel = (
    <Label
      htmlFor={field.id || field.name}
      optional={field.optional && !field.hideOptional}
      required={field.showRequired && field.required}
      tooltip={field.tooltip}
      tags={field.tags}
      description={field.labelDescription}
      className={isDesignFormField ? '!text-xs' : undefined}
    >
      {field.label}
    </Label>
  );

  return (
    // A plain wrapper, not FormElement: the config-driven schema computes each
    // field's margin below, and FormElement also hardcodes its own mb-4 — together
    // they produced a duplicate `mb-4 mb-4` (or `mb-4 mb-2!`) on every field. Letting
    // this wrapper own spacing keeps exactly one margin class per field.
    <div
      className={clsx({
        'mb-4': isRegularMargin && !isTightCheckboxGap,
        'mb-2!': isTightCheckboxGap,
        'mb-0!': isNoMargin,
        'mb-3!': isReduceMargin && !isNoMargin && !isTightCheckboxGap,
        'ml-[26px]': field.inset,
        hidden: isFieldHidden,
        'mb-8!': field.increaseMargin,
        relative: field.isRelative,
      })}
    >
      {showLabel && field.showCount && field.maxLength ? (
        <div className="flex items-baseline justify-between gap-2">
          {fieldLabel}
          <span className="text-xs text-gray-500 dark:text-muted-foreground">
            {String(get(values, field.name) ?? '').length}/{field.maxLength}
          </span>
        </div>
      ) : (
        showLabel && fieldLabel
      )}

      <FormField
        field={field}
        values={values}
        inputClassName={inputClassName}
        setFieldValue={setFieldValue}
        touched={touched}
        initialValues={initialValues}
        errors={errors}
        renderField={renderField}
      />

      {field.additionalContent && typeof field.additionalContent === 'function'
        ? field.additionalContent(values)
        : field.additionalContent}

      {field.helpText && (
        <div
          className={clsx(
            'block text-gray-500 dark:text-muted-foreground font-normal text-xs pointer mt-1',
            {
              'pl-[26px]': field.type === 'checkbox',
            },
          )}
        >
          {typeof field.helpText === 'function' ? field.helpText(values) : field.helpText}
        </div>
      )}

      {field.name && (
        <FormErrorFromProps
          error={getNestedValue(errors, field.name)}
          touched={getNestedValue(touched, field.name)}
        />
      )}
    </div>
  );
}

export const Field = React.memo(FieldInner, fieldPropsAreEqual);

export default Form;
